import { useEffect, useState } from "react";

import { FormControlLabel } from "@mui/material";

import { useZusParams, ZusParamsFace } from "../";
import { TRANSLATE_PREFIX } from "../..";
import { EnumComparisonOperators } from "../../../../../api/types";
import { AssetMultiSelection } from "../../../../../components/AssetSelection";
import { ChainSingleSelection } from "../../../../../components/ChainSelection";
import { useDatePicker } from "../../../../../components/DatePicker";
import { useAssets, useChains } from "../../../../../components/FetchConfig";
import GeneralBtn from "../../../../../components/GeneralBtn";
import {
  NodeTypesEnumMultiSelection,
  SingleSelection,
} from "../../../../../components/GeneralSelection";
import MpTextField from "../../../../../components/MpTextField";
import MpTextFieldMultiple from "../../../../../components/MpTextFieldMultiple";
import {
  Box,
  Checkbox,
  SelectChangeEvent,
} from "../../../../../components/MuiGenerals";
import { useAlerting, useTranslation } from "../../../../../hooks";
import { selectCrossMerchant } from "../../../../../reducer/crossMerchantSlice";
import { useAppSelector } from "../../../../../reducer/hooks";
import {
  containsOnlyNumbers,
  getDecimal,
  toDBInDecimals,
} from "../../../../../utils/helper";
import { genField } from "../../../../../utils/HelperComp";
import { customSx } from "../../../../../utils/styling";

interface Ifields {
  chain_name: string;
  asset_names: string[];
  client_id: string;
  comparison_operator: string;
  comparison_value: string;
  is_zero_excluded: boolean;
  liquidity_providers: string[];
  merchants: string[];
}

const initFields = {
  chain_name: "",
  asset_names: [],
  client_id: "",
  comparison_operator: "",
  comparison_value: "",
  is_zero_excluded: false,
  liquidity_providers: [],
  merchants: [],
};

const initZusParams = {
  page: 0,
  pageSize: 20,
} as const;

export function Filter() {
  const [fields, setFields] = useState<Ifields>(initFields);
  const { t } = useTranslation(TRANSLATE_PREFIX);
  const zusParams = useZusParams();
  const { alerting } = useAlerting();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const DateObj = {
    CreationTime: useDatePicker(),
    LastModifiedDate: useDatePicker(),
  };

  const { liquidityProvidersMapping, merchantMapping } =
    useAppSelector(selectCrossMerchant);

  const getParams: () => ZusParamsFace = () => {
    const clientIdRawArr = fields.client_id.split(",");

    const client_ids = clientIdRawArr
      .filter((item) => item)
      .map((item) => item.trim());
    const {
      asset_names,
      chain_name,
      liquidity_providers,
      is_zero_excluded,
      comparison_operator,
      comparison_value,
    } = fields;
    const { chain_type, chain_id } = selectChainByPieces({ chain_name });
    const { CreationTime, LastModifiedDate } = DateObj;
    const { start: date_created_from, end: date_created_to } = CreationTime;
    const { start: date_modified_from, end: date_modified_to } =
      LastModifiedDate;

    const exclude_zero = is_zero_excluded ? "true" : "false";

    const decimals = getDecimal({
      chain_type: String(chain_type),
      chain_id: String(chain_id),
      asset_name: asset_names[0],
    });

    const balance_comparison = toDBInDecimals(comparison_value || 0, decimals);

    return {
      ...initZusParams,
      client_ids,
      chain_id,
      chain_type,
      asset_names,
      date_created_from,
      date_created_to,
      date_modified_from,
      date_modified_to,
      liquidity_providers,
      merchant_ids: fields.merchants,
      exclude_zero,
      comparison_operator,
      balance_comparison,
    };
  };

  const apiParams = getParams();

  const onChange =
    (type: keyof Ifields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      const value = e.target.value;
      const hasInvalidValue =
        type === "comparison_value" && !containsOnlyNumbers(value);
      if (hasInvalidValue) {
        return;
      }
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const onSearch = () => {
    const hasFilledSomething =
      fields.comparison_operator || fields.comparison_value;
    const isIncomplete =
      fields.comparison_operator === "" || fields.comparison_value === "";
    if (hasFilledSomething && isIncomplete) {
      return alerting(
        "warning",
        t("cb_both_fields_in_client_balance_are_required")
      );
    }
    if (
      fields.comparison_operator === "eq" &&
      Number(fields.comparison_value) === 0 &&
      fields.is_zero_excluded
    ) {
      return alerting(
        "warning",
        t("cb_should_not_be_zero_as_zero_is_excluded")
      );
    }

    zusParams.setBody({ ...apiParams, page: 0 });
    zusParams.refetch();
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.CreationTime.clearDate();
    DateObj.LastModifiedDate.clearDate();
  };
  const allAssetNames = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });
  useEffect(
    () => setFields((f) => ({ ...f, asset_names: [] })),
    [fields.chain_name]
  );
  useEffect(() => {
    if (fields.asset_names.length === 1) {
      return;
    }

    setFields((f) => ({
      ...f,
      comparison_operator: "",
      comparison_value: "",
    }));
  }, [fields.asset_names]);

  const F = genField({ t }, [
    ["creation_time", <DateObj.CreationTime.Picker type="dateTime" />],
    ["last_modified_date", <DateObj.LastModifiedDate.Picker type="dateTime" />],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("ph_chain_name")}
        setChoice={(chain_name) => setFields((f) => ({ ...f, chain_name }))}
        choice={fields.chain_name}
      />,
    ],
    [
      "currency",

      <AssetMultiSelection
        label={t("ph_currency")}
        setChoices={(asset_names) => setFields((f) => ({ ...f, asset_names }))}
        choices={fields.asset_names}
        allItems={allAssetNames}
      />,
    ],
    [
      "liquidity_provider",
      <NodeTypesEnumMultiSelection
        label={t("ph_liquidity_provider")}
        onChange={(liquidityProviders) =>
          setFields({
            ...fields,
            liquidity_providers: liquidityProviders,
          })
        }
        value={fields.liquidity_providers}
        enumData={liquidityProvidersMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      "merchant",
      <NodeTypesEnumMultiSelection
        label={t("ph_merchant")}
        onChange={(merchants) =>
          setFields({
            ...fields,
            merchants,
          })
        }
        value={fields.merchants}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      "client_id",
      <MpTextFieldMultiple
        value={fields.client_id}
        onChange={onChange("client_id")}
        label={t("ph_client_id")}
        count={apiParams.client_ids.length}
      />,
    ],
    [
      "client_balance",
      <Box sx={{ display: "flex", columnGap: "1rem" }}>
        <SingleSelection
          label=""
          disabled={fields.asset_names.length !== 1} // disabled when it is not selected single asset name
          value={fields.comparison_operator}
          onChange={onChange("comparison_operator")}
          enumData={EnumComparisonOperators}
          clearSelect={() =>
            setFields((f) => ({ ...f, comparison_operator: "" }))
          }
        />
        <MpTextField
          name={"comparison_value"}
          label={t("ph_client_balance")}
          disabled={fields.asset_names.length !== 1} // disabled when it is not selected single asset name
          value={fields.comparison_value}
          onChange={onChange("comparison_value")}
        />
        <FormControlLabel
          value="yes"
          control={<Checkbox />}
          label={t("exclude_zero")}
          labelPlacement="start"
          checked={fields.is_zero_excluded}
          onChange={(_, checked) =>
            setFields((prev) => ({ ...prev, is_zero_excluded: checked }))
          }
          sx={{ minWidth: "fit-content" }}
        />
      </Box>,
    ],
  ]);
  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.creation_time}
        {F.last_modified_date}
        {F.chain_name}
        {F.currency}
        {F.liquidity_provider}
        {F.merchant}
        {F.client_id}
        {F.client_balance}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}
